import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import NoteActions from './NoteActions';
import ConversationAttachments from './ConversationAttachments';
import styles from './Conversations.module.css';
import { getUserProfileImage, createMarkup } from '../../../../helpers/generic';
import { splitClaritaskCommentFiles } from '../../../../helpers/claritaskCommentFiles';
import { baseURL } from '../../../../helpers/baseURL';
import { headers } from '../../../../helpers/Requests';
import moment from 'moment';
import { formatDateTime } from '../../../../helpers/formatDate';

const SeenByAvatars = ({ entries }) => {
    if (!entries || entries.length === 0) return null;
    return (
        <div className={styles.seenBy}>
            {entries.map((entry, idx) => (
                <OverlayTrigger
                    key={entry.user?.id ?? idx}
                    placement="top"
                    delay={{ show: 150, hide: 100 }}
                    overlay={
                        <Tooltip>
                            {`Seen by ${entry.user?.name ?? 'Unknown'}${entry.seen_at ? ` on ${formatDateTime(entry.seen_at)}` : ''}`}
                        </Tooltip>
                    }
                >
                    <span className={styles.seenByAvatar}>
                        <img src={getUserProfileImage(entry.user)} alt={entry.user?.name ?? ''} />
                    </span>
                </OverlayTrigger>
            ))}
        </div>
    );
};

const UserProfile = ({ user }) => {
    const display = user ?? { name: 'Customer', profile_image: null };
    return (
        <div className={styles.user}>
            <img src={getUserProfileImage(display)} alt="profile" style={{ width: 50, height: 50, borderRadius: '50%' }} />
            <div>{display.name}</div>
        </div>
    );
};

const formatTicketDescription = (content) => {
    if (!content || typeof content !== 'string') {
        return content;
    }
    if (content.includes('claritick-embed-form') || content.includes('<p><strong>Embed form submission</strong>')) {
        return content;
    }
    if (content.includes('Submitted via Claritick embed form') && !/<[a-z][\s\S]*>/i.test(content)) {
        return content.replace(/\n/g, '<br>');
    }
    return content;
};

const ConversationContent = ({ content, attachments, date }) => {
    const isLegacyEmbedPlain = typeof content === 'string'
        && content.includes('Submitted via Claritick embed form')
        && !content.includes('<p>');

    return (
        <div className={styles.wrapper}>
            <div
                className={`${styles.contentContainer}${isLegacyEmbedPlain ? ` ${styles.embedDescription}` : ''}`}
                dangerouslySetInnerHTML={createMarkup(formatTicketDescription(content))}
            />
            <ConversationAttachments attachments={attachments} />
            <div className={styles.date}>{formatDateTime(date)}</div>
        </div>
    );
};

const formatTransferDate = (value) => {
    if (!value) return '';
    const m = moment(value);
    if (!m.isValid()) return '';
    return m.format('MMM D, YYYY h:mmA');
};

const TicketHeader = ({ ticket }) => {
    const isTransferredOut = !!ticket?.is_transferred_out;
    const isReceived = !isTransferredOut && !!ticket?.transferred_from;
    const showTransferredChip = isTransferredOut || isReceived;

    let transferLine = null;
    if (isTransferredOut && ticket?.transferred_to) {
        const when = formatTransferDate(ticket.transferred_to.transferred_at);
        const who = ticket.transferred_to.transferred_by || 'Unknown';
        const workspace = ticket.transferred_to.workspace_name || 'another workspace';
        transferLine = when
            ? `Transferred to ${workspace} on ${when} from ${who}`
            : `Transferred to ${workspace} from ${who}`;
    } else if (isReceived) {
        const when = formatTransferDate(ticket.transferred_from.transferred_at);
        const workspace = ticket.transferred_from.workspace_name || 'another workspace';
        transferLine = when
            ? `Transferred on ${when} from ${workspace}`
            : `Transferred from ${workspace}`;
    }

    return (
        <div className="d-flex flex-column align-items-start">
            <div className="d-flex flex-wrap align-items-center gap-2">
                <b>{ticket?.title}</b>
                {showTransferredChip ? (
                    <span className="badge" style={{ backgroundColor: '#f59e0b', color: '#fff' }}>Transferred</span>
                ) : null}
            </div>
            <span className='opacity-75'>{`#${ticket?.ticket_id}`}</span>
            {transferLine ? (
                <span className="text-muted small mt-1 mb-2">{transferLine}</span>
            ) : null}
        </div>
    );
};

// Claritask comments are chat-style (oldest → newest); Conversations/Notes are newest-first.
const SCROLL_TO_BOTTOM_TYPES = new Set(['claritask']);

const scrollToLatest = (container) => {
    if (!container) return;
    const messages = container.querySelectorAll('[data-conversation-item]');
    const target = messages.length > 0 ? messages[messages.length - 1] : container;
    target.scrollIntoView({ block: 'end', behavior: 'auto' });
};

const Conversations = ({ ticket, type, onChange = () => { }, isActive = true }) => {
    const scrollRef = useRef(null);
    const baselineRef = useRef({ key: null, lastItemId: undefined });
    const scrollableTypes = SCROLL_TO_BOTTOM_TYPES.has(type);
    const items = type === 'claritask'
        ? (ticket?.claritask ?? [])
        : (ticket?.[type] ?? []);
    const lastItemId = items.length > 0 ? (items[items.length - 1]?.id ?? items.length) : null;
    // Replies/notes are newest-first (Ticket::getComments() orders by created_at desc),
    // unlike Claritask's oldest-first chat log — so "the latest one" is items[0] here,
    // not items[length-1] (that's only true for the scroll-to-bottom case above).
    const latestItemId = items.length > 0
        ? (scrollableTypes ? items[items.length - 1]?.id : items[0]?.id) ?? items.length
        : null;
    const ticketKey = `${ticket?.id ?? ticket?.ticket_id ?? ''}:${type}`;

    useEffect(() => {
        if (!isActive || !scrollableTypes) {
            return undefined;
        }

        const isNewContext = baselineRef.current.key !== ticketKey;
        if (isNewContext) {
            baselineRef.current = { key: ticketKey, lastItemId };
        } else if (lastItemId === baselineRef.current.lastItemId) {
            return undefined;
        } else {
            baselineRef.current.lastItemId = lastItemId;
        }

        if (lastItemId == null) {
            return undefined;
        }

        let cancelled = false;
        const timeouts = [];

        const scroll = () => {
            if (cancelled) return;
            scrollToLatest(scrollRef.current);
        };

        const scheduleScroll = () => {
            scroll();
            requestAnimationFrame(() => requestAnimationFrame(scroll));
            timeouts.push(setTimeout(scroll, 50), setTimeout(scroll, 200));
        };

        if (scrollRef.current) {
            scheduleScroll();
        } else {
            requestAnimationFrame(scheduleScroll);
        }

        return () => {
            cancelled = true;
            timeouts.forEach(clearTimeout);
        };
    }, [scrollableTypes, isActive, type, items.length, lastItemId, ticketKey]);

    // Advance this user's seen-watermark for this stream (replies, notes, and
    // Claritask comments all tracked separately) whenever the tab is active
    // and there's something new to mark seen — mirrors Claritask's
    // mark-on-view/mark-on-poll. For 'claritask_comment', this is a
    // Claritick-local watermark only ("has this Claritick user viewed it
    // here"), not a mirror of Claritask's own native seen-by state.
    const seenApiType = type === 'notes' ? 'note' : (type === 'replies' ? 'reply' : (type === 'claritask' ? 'claritask_comment' : null));
    const seenRef = useRef({ key: null, lastItemId: undefined });
    useEffect(() => {
        if (!isActive || !seenApiType || !ticket?.id || latestItemId == null) {
            return;
        }
        const seenKey = `${ticket.id}:${seenApiType}`;
        if (seenRef.current.key === seenKey && seenRef.current.lastItemId === latestItemId) {
            return;
        }
        seenRef.current = { key: seenKey, lastItemId: latestItemId };
        // last_seen_comment_id is required for 'claritask_comment' (an
        // external id we can't recompute server-side) and harmlessly ignored
        // for reply/note, which the backend recomputes itself via MAX(id) on
        // our own table.
        axios.post(`${baseURL}tickets/${ticket.id}/comments-seen`, { type: seenApiType, last_seen_comment_id: latestItemId }, headers)
            .catch(() => { });
    }, [isActive, seenApiType, ticket?.id, latestItemId]);

    const seenByType = ticket?.comment_seens?.[seenApiType] ?? [];

    const renderConversations = (conversations, isNote) => (
        conversations.length > 0 ? conversations.map((conversation) => (
            <div
                key={conversation.id}
                className={styles.boxContainer}
                data-conversation-item
                data-role={conversation.role === 'customer' ? 'customer' : 'agent'}
                data-type={isNote ? 'note' : 'reply'}
            >
                <div className={styles.header}>
                    <UserProfile user={conversation.user} />
                    <ConversationContent content={conversation.content} attachments={conversation.attachments || []} date={conversation.created_at} />
                    {isNote && !ticket?.is_transferred_out && <NoteActions note={conversation} onChange={onChange} />}
                </div>
                <SeenByAvatars entries={seenByType.filter(entry => Number(entry.last_seen_comment_id) === Number(conversation.id))} />
            </div>
        )) : <div className='d-flex justify-content-center my-2'><div className='text-muted'>No {type} yet</div></div>
    );

    const renderClaritaskComments = () => {
        if (!ticket.claritask || ticket.claritask.length === 0) {
            return <div className='text-muted text-center my-2'>No Claritask comments yet</div>;
        }

        const firstComment = ticket.claritask[0];

        return (
            <div style={{ position: 'relative', paddingTop: '30px' }}>
                {firstComment.task_id && (
                    <div style={{ position: 'absolute', top: 0, right: 0 }}>
                        <a
                            className="btn btn-primary"
                            href={`http://claritask.com/a/workspace/${firstComment.workspace_id}/project/${firstComment.project_id}/board/${firstComment.board_id}/${firstComment.route_type}?target=${firstComment.task_id}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12 }}
                        >
                            View Task in Claritask
                        </a>
                    </div>
                )}

                {ticket.claritask.map(comment => {
                    const { content, attachments } = splitClaritaskCommentFiles(comment);
                    return (
                    <div key={comment.id} className={styles.boxContainer} data-conversation-item data-role="agent" data-type="claritask">
                        <div className={styles.header}>
                            <UserProfile user={comment.user} />
                            <ConversationContent content={content} attachments={attachments} date={comment.created_at} />
                        </div>
                        <SeenByAvatars entries={seenByType.filter(entry => Number(entry.last_seen_comment_id) === Number(comment.id))} />
                    </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={styles.container} data-type={type}>
            {type === 'ticket' ? (
                <div className={styles.boxContainer}>
                    <div className={styles.header}>
                        {ticket?.created_by && <UserProfile user={ticket.created_by} />}
                        <div className={styles.wrapper}>
                            <TicketHeader ticket={ticket} />
                            <ConversationContent content={ticket?.description} attachments={ticket?.attachments} date={ticket?.created_at} />
                        </div>
                    </div>
                </div>
            ) : (
                <div ref={scrollRef} className={styles.messages}>
                    {type === 'claritask' ? renderClaritaskComments() : renderConversations(items, type === 'notes')}
                </div>
            )}
        </div>
    );
};

export default Conversations;
